import React, { useState, useEffect, useCallback } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';
import toast from 'react-hot-toast';
import { useLanguage } from '../../../contexts/LanguageContext';

const buildPrompt = (data) => {
  const address = [data?.address, data?.suburb, data?.state].filter(Boolean).join(', ');
  const storyAnswers = data?.storyAnswers || {};

  return `You are an expert real estate copywriter. Generate compelling property listing copy for the following property.

Property Details:
- Property Type: ${data?.type || 'residential'}
- Bedrooms: ${data?.beds || 'N/A'}
- Bathrooms: ${data?.baths || 'N/A'}
- Parking Spaces: ${data?.parking || 'N/A'}
- Address: ${address || 'N/A'}
- Listing Type: ${data?.listingType === 'rent' ? 'For Rent' : 'For Sale'}

Agent/Owner Story:
- What they love most: ${storyAnswers?.whatYouLove || 'Not provided'}
- Recent renovations/improvements: ${storyAnswers?.renovations || 'Not provided'}
- Ideal buyer/tenant: ${storyAnswers?.idealBuyer || 'Not provided'}

Generate the following in JSON format:
1. "headlines": An array of exactly 3 compelling, unique headlines (each 10-15 words). Each headline should take a different angle: emotional appeal, lifestyle benefit, and investment value.
2. "shortDescription": A punchy 50-75 word description perfect for listing portals and social media. Focus on the top 3 selling points.
3. "fullDescription": A detailed 180-220 word description optimized for attracting buyers. Include lifestyle benefits, key features, location advantages, and a compelling call to action.

Respond ONLY with valid JSON. No markdown, no extra text. Example format:
{
  "headlines": ["Headline one here", "Headline two here", "Headline three here"],
  "shortDescription": "Short description text here...",
  "fullDescription": "Full description text here..."
}`;
};

const StepGenerateCopy = ({ data, onNext, onBack }) => {
  const [generating, setGenerating] = useState(false);
  const [generatedCopy, setGeneratedCopy] = useState(data?.generatedCopy?.headlines?.length > 0 ? data.generatedCopy : null);
  const [selectedHeadline, setSelectedHeadline] = useState(0);
  const [editableShort, setEditableShort] = useState('');
  const [editableFull, setEditableFull] = useState('');
  const [error, setError] = useState('');
  const { t } = useLanguage();

  useEffect(() => {
    if (generatedCopy) {
      setEditableShort(generatedCopy?.shortDescription || '');
      setEditableFull(generatedCopy?.fullDescription || '');
    }
  }, [generatedCopy]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError('');
    try {
      const messages = [
        {
          role: 'system',
          content: 'You are an expert real estate copywriter. Always respond with valid JSON only, no markdown or extra text.'
        },
        {
          role: 'user',
          content: buildPrompt(data)
        }
      ];

      const result = await getChatCompletion(
        'OPEN_AI',
        'gpt-5',
        messages,
        {
          max_completion_tokens: 1200,
          response_format: { type: 'json_object' }
        }
      );

      const content = result?.choices?.[0]?.message?.content || '';
      let parsed;
      try {
        parsed = JSON.parse(content);
      } catch {
        // Try to extract JSON from the response
        const jsonMatch = content?.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          parsed = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('Invalid response format from AI. Please try again.');
        }
      }

      if (!parsed?.headlines || !parsed?.shortDescription || !parsed?.fullDescription) {
        throw new Error('Incomplete response from AI. Please try again.');
      }

      setGeneratedCopy(parsed);
      setEditableShort(parsed?.shortDescription || '');
      setEditableFull(parsed?.fullDescription || '');
      setSelectedHeadline(0);
      toast.success('Listing copy generated successfully!');
    } catch (err) {
      const msg = err?.message || 'Failed to generate copy. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  }, [data]);

  const handleCopy = (text, label) => {
    navigator?.clipboard?.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard!`);
    }).catch(() => {
      toast.error('Failed to copy to clipboard.');
    });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onNext({
      generatedCopy: {
        ...generatedCopy,
        selectedHeadline: generatedCopy?.headlines?.[selectedHeadline],
        shortDescription: editableShort,
        fullDescription: editableFull
      }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{t('generateCopyTitle')}</h2>
        <p className="text-muted-foreground">{t('generateCopyDesc')}</p>
      </div>

      {!generatedCopy ? (
        <div className="text-center py-12">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Icon name="Sparkles" size={40} color="var(--color-primary)" />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-3">{t('readyToGenerate')}</h3>
          <p className="text-muted-foreground mb-2 max-w-md mx-auto">
            {t('readyToGenerateDesc')}
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {[t('chooseHeadline'), t('shortDescriptionLabel'), t('fullDescriptionLabel')].map((item) => (
              <span key={item} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-3 py-1 rounded-full">
                <Icon name="Check" size={12} color="currentColor" />
                {item}
              </span>
            ))}
          </div>
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-3 mb-4 max-w-md mx-auto">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}
          <Button
            type="button"
            variant="default"
            size="lg"
            onClick={handleGenerate}
            loading={generating}
            iconName="Sparkles"
          >
            {generating ? t('generatingCopy') : t('generateCopyWithAI')}
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Headlines Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-foreground">{t('chooseHeadline')}</label>
              <span className="text-xs text-muted-foreground">{t('selectPreferredHeadline')}</span>
            </div>
            <div className="space-y-2">
              {generatedCopy?.headlines?.map((headline, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setSelectedHeadline(index)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all group ${
                    selectedHeadline === index
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                        selectedHeadline === index ? 'border-primary bg-primary' : 'border-muted-foreground'
                      }`}>
                        {selectedHeadline === index && (
                          <Icon name="Check" size={12} color="white" />
                        )}
                      </div>
                      <p className="font-semibold text-foreground leading-snug">{headline}</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleCopy(headline, 'Headline'); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded"
                      title="Copy headline"
                    >
                      <Icon name="Copy" size={14} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Short Description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-foreground">{t('shortDescriptionLabel')}</label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{editableShort?.length || 0} chars</span>
                <button
                  type="button"
                  onClick={() => handleCopy(editableShort, 'Short description')}
                  className="p-1 hover:bg-muted rounded transition-colors"
                  title="Copy short description"
                >
                  <Icon name="Copy" size={14} color="var(--color-muted-foreground)" />
                </button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t('shortDescriptionHint')}</p>
            <textarea
              value={editableShort}
              onChange={(e) => setEditableShort(e?.target?.value)}
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              placeholder="Short description will appear here..."
            />
          </div>

          {/* Full Description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-foreground">{t('fullDescriptionLabel')}</label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{editableFull?.length || 0} chars</span>
                <button
                  type="button"
                  onClick={() => handleCopy(editableFull, 'Full description')}
                  className="p-1 hover:bg-muted rounded transition-colors"
                  title="Copy full description"
                >
                  <Icon name="Copy" size={14} color="var(--color-muted-foreground)" />
                </button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t('fullDescriptionHint')}</p>
            <textarea
              value={editableFull}
              onChange={(e) => setEditableFull(e?.target?.value)}
              rows={8}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              placeholder="Full description will appear here..."
            />
          </div>

          <div className="flex justify-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerate}
              loading={generating}
              iconName="RefreshCw"
            >
              {generating ? t('generatingCopy') : t('regenerate')}
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack} iconName="ArrowLeft">{t('back')}</Button>
        {generatedCopy && (
          <Button type="submit" variant="default" iconName="ArrowRight" iconPosition="right">{t('continue')}</Button>
        )}
      </div>
    </form>
  );
};

export default StepGenerateCopy;